import { Card, CardContent, CardHeader } from '@/components/ui';
import type { ErrorListItem } from '@/types/api';

import { ResolutionPanel } from '../ErrorDetail';
import { GuidancePanel } from './GuidancePanel';

/** Spec S08 §"Layout H-type" — halt review with DRC viewer + 3 dedicated actions. */
export function HaltReviewLayout({ error }: { error: ErrorListItem }) {
  const payload = error.payload as Record<string, unknown>;
  const haltType = String(payload.halt_type ?? error.error_subtype ?? 'unknown');
  const detail = (payload.detail as Record<string, unknown> | undefined) ?? {};

  return (
    <div className="space-y-3 p-3">
      <Card className="border-red-200 bg-red-50/40">
        <CardHeader>
          <h2 className="text-h2 text-red-700">Document halted — {haltType}</h2>
        </CardHeader>
        <CardContent className="text-body text-slate-700">
          <dl className="grid grid-cols-3 gap-y-1 text-small">
            <Row label="Agent" value={String(payload.agent ?? '—')} />
            <Row label="Halt reason" value={String(detail.halt_reason ?? haltType)} />
            <Row
              label="Composite"
              value={
                detail.composite != null ? String(detail.composite) : '—'
              }
            />
            <Row
              label="Ontology score"
              value={
                detail.ontology_score != null ? String(detail.ontology_score) : '—'
              }
            />
            <Row
              label="Threshold"
              value={
                detail.effective_threshold != null
                  ? String(detail.effective_threshold)
                  : '—'
              }
            />
            <Row label="Source" value={error.source_id?.slice(0, 8) ?? '—'} />
          </dl>
        </CardContent>
      </Card>

      <div className="grid grid-cols-[2fr_1fr] gap-3">
        <Card>
          <CardHeader>
            <h3 className="text-h3 text-brand-900">DRC (collapsed view)</h3>
          </CardHeader>
          <CardContent>
            <pre className="max-h-96 overflow-auto rounded bg-slate-50 p-3 text-small text-slate-700">
              {JSON.stringify(payload, null, 2)}
            </pre>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-h3 text-brand-900">Analyst actions</h3>
          </CardHeader>
          <CardContent>
            {error.guidance && <GuidancePanel guidance={error.guidance} />}
            <ResolutionPanel
              error={error}
              availableActions={['fix_and_accept', 'cancel', 'escalate']}
              decisionTypes={{
                fix_and_accept: ['halt_override'],
                cancel: ['halt_confirmed'],
                escalate: ['skill_escalation'],
                suspend: ['deferred'],
              }}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="col-span-2 truncate font-mono text-slate-700" title={value}>
        {value}
      </dd>
    </>
  );
}
